import type { AiCreditTransaction } from '@ztechapps/common.contract'
import type { BodyRowConfig } from '@ztechapps/webui.data-tools'
import { RowNumberBodyCell } from '@ztechapps/webui.data-tools'
import { LocalDateTime } from '@ztechapps/webui.date'
import { formatNumber } from '@ztechapps/common.utils'
import { formatCreditAmount } from '@ztechapps/ai.common'
import { HorizontalStack, Icon, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { USAGE_CREDIT_DECIMALS } from './constants'
import { useUsageListContext } from './Context'
import { UserCell } from './UserCell'
import styles from './ListRow.module.scss'

export const ListRow = (props: BodyRowConfig<AiCreditTransaction>) => {
  const {
    createdAt,
    createdByName,
    createdByEmail,
    feature,
    remarks,
    model,
    inputTokens,
    outputTokens,
    rawUsd,
    markupCcy,
    markupPct,
    credits,
    balanceAfter,
  } = props
  const { currencyFormat } = useUsageListContext()
  const symbol = currencyFormat?.symbol

  return (
    <>
      <RowNumberBodyCell {...props} />

      {props._renderDataBodyCell({
        createdAt: <LocalDateTime value={createdAt} />,
        createdByName: <UserCell name={createdByName} email={createdByEmail} />,
        feature,
        remarks,
        model,
        inputTokens: formatNumber(inputTokens),
        outputTokens: formatNumber(outputTokens),
        totalTokens: formatNumber((inputTokens ?? 0) + (outputTokens ?? 0)),
        // rawUsd stays at 6dp (not formatNumber4) — a per-call cost of a fraction of a cent
        // would round to 0.0000 at 4dp.
        rawUsd: rawUsd?.toFixed(6) ?? '—',
        markupCcy: (
          <HorizontalStack gap={1}>
            <div>{formatCreditAmount(markupCcy, { symbol, decimals: USAGE_CREDIT_DECIMALS })}</div>
            <ShowIf condition={markupPct !== undefined}>
              <TooltipPopOver tooltipBody={`${formatNumber(markupPct)}% markup applied`}>
                <div className={styles.markupInfo}>
                  <Icon name="info-circle" />
                </div>
              </TooltipPopOver>
            </ShowIf>
          </HorizontalStack>
        ),
        // This table is scoped server-side to `type: Debit` rows only (`GetAiCreditTransactionList`
        // via `scopeToOwnerDebitFilter`) — post-merge, `credits` is signed negative on every row here (it was always
        // positive on the old, Debit-only `AiUsageRecord`). The sign carries no information when
        // every row is the same type, so it's displayed as the magnitude — "credits this call cost" —
        // matching pre-merge behavior. The school Ledger tab (schools/item/tabs/ledger) mixes
        // Debit/Grant/Adjustment/Purchase rows in one table, so it keeps the sign and labels its
        // footer "Net Credits" instead.
        credits: formatCreditAmount(Math.abs(credits), { symbol, decimals: USAGE_CREDIT_DECIMALS }),
        // Account currency's own `decimals` (not the fixed 4dp USAGE_CREDIT_DECIMALS above) —
        // matches the school Ledger tab's `balanceAfter` rendering (ledger/ListRow.tsx), whose
        // constants.ts explains this field predates the fixed-precision usage columns.
        balanceAfter: formatCreditAmount(balanceAfter, { symbol, decimals: currencyFormat?.decimals }),
      })}
    </>
  )
}
